import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { formatContainerDisplayId } from '../lib/batch';
import { formatQty } from '../lib/quantity';
import { extractionsApi, type ExtractionDetails, type ExtractionListRow, type ExtractionMeta } from '../services/extractions';

type OutputDraft = {
  id?: number;
  qtyKg: string;
  waterPercent: string;
  statusItemId: number | '';
};

type DocumentDraft = {
  title: string;
  documentDate: string;
  notes: string;
};

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const pos = result.indexOf(',');
      resolve(pos >= 0 ? result.slice(pos + 1) : result);
    };
    reader.onerror = () => reject(new Error('Kunde inte läsa filen'));
    reader.readAsDataURL(file);
  });
}

export function ExtractionsPage() {
  const toast = useToast();
  const { canWrite } = useAuth();
  const writable = canWrite('extractions');
  const currentYear = new Date().getFullYear();

  const [rows, setRows] = useState<ExtractionListRow[]>([]);
  const [meta, setMeta] = useState<ExtractionMeta | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [details, setDetails] = useState<ExtractionDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [posting, setPosting] = useState(false);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [updatingContainerId, setUpdatingContainerId] = useState<number | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [savingExtraction, setSavingExtraction] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [deletingDocumentId, setDeletingDocumentId] = useState<number | null>(null);

  const [formDate, setFormDate] = useState(new Date().toISOString().slice(0, 10));
  const [formApiarySiteId, setFormApiarySiteId] = useState('');
  const [formNectarItemId, setFormNectarItemId] = useState('');
  const [formProductId, setFormProductId] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [outputs, setOutputs] = useState<OutputDraft[]>([{ qtyKg: '', waterPercent: '', statusItemId: '' }]);

  const [documentDraft, setDocumentDraft] = useState<DocumentDraft>({
    title: '',
    documentDate: '',
    notes: '',
  });

  const productOptions = useMemo(
    () => (meta?.products ?? []).filter((product) => product.kind === 'BULK' || product.kind === 'INSTANCE'),
    [meta]
  );

  const defaultProductionSiteId = meta?.defaultProductionSiteId ?? meta?.sites[0]?.id ?? null;

  const toFixedDraft = (value: string, decimals: number) => {
    const normalized = value.replace(',', '.').trim();
    if (normalized === '') {
      return '';
    }
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed.toFixed(decimals) : value;
  };

  const statusLabel = (status: 'DRAFT' | 'POSTED') => (status === 'POSTED' ? 'Bokförd' : 'Utkast');

  const resetForm = () => {
    setFormDate(new Date().toISOString().slice(0, 10));
    setFormApiarySiteId('');
    setFormNectarItemId('');
    setFormProductId(productOptions[0]?.id ? String(productOptions[0].id) : '');
    setFormNotes('');
    setOutputs([{ qtyKg: '', waterPercent: '', statusItemId: '' }]);
  };

  const load = async (year: number) => {
    setLoading(true);
    try {
      const [listRes, metaRes] = await Promise.all([extractionsApi.list(year), extractionsApi.meta()]);
      setRows(listRes.extractions);
      setMeta(metaRes);
      setSelectedId(null);
      setDetails(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ladda slungning');
    } finally {
      setLoading(false);
    }
  };

  const loadDetails = async (id: number) => {
    setLoadingDetails(true);
    try {
      const res = await extractionsApi.get(id);
      setDetails(res);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ladda slungningsdetalj');
      setDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  useEffect(() => {
    void load(selectedYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  useEffect(() => {
    if (!selectedId) {
      setDetails(null);
      return;
    }
    void loadDetails(selectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const openEdit = () => {
    if (!details) {
      return;
    }
    setFormDate(String(details.extractionDate).slice(0, 10));
    setFormApiarySiteId(details.apiarySiteId ? String(details.apiarySiteId) : '');
    setFormNectarItemId(details.nectarItemId ? String(details.nectarItemId) : '');
    setFormProductId(details.outputs[0]?.productId ? String(details.outputs[0].productId) : '');
    setFormNotes(details.notes ?? '');
    setOutputs(
      details.outputs.map((output) => ({
        id: output.id,
        qtyKg: Number(output.qtyKg).toFixed(2),
        waterPercent: output.waterPercent === null ? '' : Number(output.waterPercent).toFixed(2),
        statusItemId: output.statusItemId ?? '',
      }))
    );
    setEditOpen(true);
  };

  const createExtraction = async () => {
    if (!defaultProductionSiteId) {
      toast.error('Ingen standardplats finns för slungning');
      return;
    }

    const selectedProductId = Number(formProductId);
    if (!selectedProductId) {
      toast.error('Välj produkt i huvudet');
      return;
    }

    const preparedOutputs = outputs.map((output) => ({
      productId: selectedProductId,
      qtyKg: Number(output.qtyKg),
      waterPercent: output.waterPercent.trim() === '' ? null : Number(output.waterPercent),
      statusItemId: output.statusItemId === '' ? null : Number(output.statusItemId),
    }));

    for (const output of preparedOutputs) {
      if (Number.isNaN(output.qtyKg) || output.qtyKg <= 0) {
        toast.error('Varje outputrad måste ha kg > 0');
        return;
      }
    }

    setSavingExtraction(true);
    try {
      const created = await extractionsApi.create({
        extractionDate: formDate,
        apiarySiteId: formApiarySiteId ? Number(formApiarySiteId) : null,
        productionSiteId: defaultProductionSiteId,
        nectarItemId: formNectarItemId ? Number(formNectarItemId) : null,
        notes: formNotes,
        outputs: preparedOutputs,
      });
      toast.success(`Slungning skapad: ${created.extractionCode}`);
      setCreateOpen(false);
      resetForm();
      await load(selectedYear);
      setSelectedId(created.id);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte skapa slungning');
    } finally {
      setSavingExtraction(false);
    }
  };

  const saveEdit = async () => {
    if (!details) {
      return;
    }

    const selectedProductId = Number(formProductId);
    if (!selectedProductId) {
      toast.error('Välj produkt i huvudet');
      return;
    }

    const preparedOutputs = outputs.map((output) => ({
      ...(output.id ? { id: output.id } : {}),
      productId: selectedProductId,
      qtyKg: Number(output.qtyKg),
      waterPercent: output.waterPercent.trim() === '' ? null : Number(output.waterPercent),
      statusItemId: output.statusItemId === '' ? null : Number(output.statusItemId),
    }));

    for (const output of preparedOutputs) {
      if (Number.isNaN(output.qtyKg) || output.qtyKg <= 0) {
        toast.error('Varje outputrad måste ha kg > 0');
        return;
      }
    }

    setSavingExtraction(true);
    try {
      await extractionsApi.update(details.id, {
        extractionDate: formDate,
        apiarySiteId: formApiarySiteId ? Number(formApiarySiteId) : null,
        nectarItemId: formNectarItemId ? Number(formNectarItemId) : null,
        notes: formNotes,
        outputs: preparedOutputs,
      });
      toast.success('Slungning uppdaterad');
      setEditOpen(false);
      await load(selectedYear);
      await loadDetails(details.id);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte uppdatera slungning');
    } finally {
      setSavingExtraction(false);
    }
  };

  const postExtraction = async () => {
    if (!details) {
      return;
    }
    setPosting(true);
    try {
      await extractionsApi.post(details.id);
      toast.success('Slungning bokförd');
      await load(selectedYear);
      await loadDetails(details.id);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte bokföra slungning');
    } finally {
      setPosting(false);
    }
  };

  const updateOutputStatus = async (containerId: number, statusItemId: number) => {
    setUpdatingContainerId(containerId);
    try {
      await extractionsApi.updateOutputStatus(containerId, statusItemId);
      toast.success('Batchstatus uppdaterad');
      if (selectedId) {
        await loadDetails(selectedId);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte uppdatera batchstatus');
    } finally {
      setUpdatingContainerId(null);
    }
  };

  const uploadDocument = async (file: File) => {
    if (!details) {
      return;
    }
    setUploadingDocument(true);
    try {
      const contentBase64 = await toBase64(file);
      await extractionsApi.uploadDocument(details.id, {
        filename: file.name,
        mimeType: file.type || 'application/octet-stream',
        contentBase64,
        title: documentDraft.title.trim() || undefined,
        documentDate: documentDraft.documentDate.trim() || null,
        notes: documentDraft.notes.trim() || undefined,
      });
      setDocumentDraft({ title: '', documentDate: '', notes: '' });
      toast.success('Analysdokument uppladdat');
      await loadDetails(details.id);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ladda upp dokument');
    } finally {
      setUploadingDocument(false);
    }
  };

  const deleteDocument = async (documentId: number) => {
    setDeletingDocumentId(documentId);
    try {
      await extractionsApi.deleteDocument(documentId);
      toast.success('Dokument borttaget');
      if (details) {
        await loadDetails(details.id);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ta bort dokument');
    } finally {
      setDeletingDocumentId(null);
    }
  };

  const renderOutputEditor = () => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium">Outputs</div>
        <button
          type="button"
          className="btn btn-secondary px-2 py-1"
          onClick={() => setOutputs((current) => [...current, { qtyKg: '', waterPercent: '', statusItemId: '' }])}
        >
          + Rad
        </button>
      </div>
      <div className="space-y-1 max-h-[320px] overflow-auto pr-1">
        {outputs.map((output, index) => (
          <div key={output.id ?? index} className="grid grid-cols-1 items-end gap-2 rounded border border-gray-200 p-2 md:grid-cols-[1.1fr_96px_96px_140px_auto]">
            <div className="text-xs">
              Hink-ID
              <input
                className="mt-1 w-full bg-gray-100"
                readOnly
                value={output.id ? String(details?.outputs.find((row) => row.id === output.id)?.containerId ?? '') : 'Sätts automatiskt'}
              />
            </div>
            <label className="text-xs">
              Kg
              <input
                type="text"
                className="mt-1 w-full"
                value={output.qtyKg}
                onChange={(event) => setOutputs((current) => current.map((row, rowIndex) => (
                  rowIndex === index ? { ...row, qtyKg: event.target.value } : row
                )))}
                onBlur={(event) => setOutputs((current) => current.map((row, rowIndex) => (
                  rowIndex === index ? { ...row, qtyKg: toFixedDraft(event.target.value, 2) } : row
                )))}
              />
            </label>
            <label className="text-xs">
              Vatten %
              <input
                type="text"
                className="mt-1 w-full"
                value={output.waterPercent}
                onChange={(event) => setOutputs((current) => current.map((row, rowIndex) => (
                  rowIndex === index ? { ...row, waterPercent: event.target.value } : row
                )))}
                onBlur={(event) => setOutputs((current) => current.map((row, rowIndex) => (
                  rowIndex === index ? { ...row, waterPercent: toFixedDraft(event.target.value, 2) } : row
                )))}
              />
            </label>
            <label className="text-xs">
              Status
              <select
                className="mt-1 w-full"
                value={output.statusItemId}
                onChange={(event) => setOutputs((current) => current.map((row, rowIndex) => (
                  rowIndex === index ? { ...row, statusItemId: event.target.value ? Number(event.target.value) : '' } : row
                )))}
              >
                <option value="">-</option>
                {(meta?.statuses ?? []).map((status) => (
                  <option key={status.id} value={status.id}>{status.label}</option>
                ))}
              </select>
            </label>
            <div className="flex justify-end">
              <button
                type="button"
                className="btn btn-secondary px-2 py-1"
                onClick={() => setOutputs((current) => current.filter((_, rowIndex) => rowIndex !== index))}
                disabled={outputs.length <= 1}
              >
                x
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-lg font-semibold">Slungning</h1>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <button type="button" className="btn btn-secondary px-2 py-1" onClick={() => setSelectedYear((prev) => prev - 1)}>PREV</button>
            <span className="min-w-16 text-center text-sm font-semibold">{selectedYear}</span>
            {selectedYear < currentYear && (
              <button type="button" className="btn btn-secondary px-2 py-1" onClick={() => setSelectedYear((prev) => Math.min(currentYear, prev + 1))}>NEXT</button>
            )}
          </div>
          {writable && (
            <button type="button" className="btn btn-primary px-2 py-1" onClick={() => { resetForm(); setCreateOpen(true); }}>
              Ny slungning
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Laddar...</p>
      ) : (
        <div className="overflow-hidden rounded border border-gray-200">
          <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 14rem)' }}>
            <table className="w-full text-sm">
              <thead className="sticky top-0 border-b border-gray-200 bg-gray-100">
                <tr>
                  <th className="px-2 py-1.5 text-left">Kod</th>
                  <th className="px-2 py-1.5 text-left">Datum</th>
                  <th className="px-2 py-1.5 text-left">Bigård</th>
                  <th className="px-2 py-1.5 text-left">Kg</th>
                  <th className="px-2 py-1.5 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className={`cursor-pointer border-t border-gray-100 hover:bg-orange-50 ${selectedId === row.id ? 'bg-orange-50' : ''}`}
                    onClick={() => setSelectedId(row.id)}
                  >
                    <td className="px-2 py-1.5">{row.extractionCode}</td>
                    <td className="px-2 py-1.5">{String(row.extractionDate).slice(0, 10)}</td>
                    <td className="px-2 py-1.5">{row.apiaryName ?? '-'}</td>
                    <td className="px-2 py-1.5">{formatQty(row.totalKg, 'kg')}</td>
                    <td className="px-2 py-1.5">{statusLabel(row.status)}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-2 py-4 text-sm text-gray-500">Inga slungningar för {selectedYear}.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {createOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-3xl space-y-3 rounded border border-gray-200 bg-white p-3">
            <h2 className="text-base font-semibold">Ny slungning</h2>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              <label className="block text-sm">
                Datum
                <input type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD" className="mt-1 w-full" value={formDate} onChange={(event) => setFormDate(event.target.value)} />
              </label>
              <label className="block text-sm">
                Bigård (valfri)
                <select className="mt-1 w-full" value={formApiarySiteId} onChange={(event) => setFormApiarySiteId(event.target.value)}>
                  <option value="">Ingen</option>
                  {(meta?.apiaries ?? []).map((apiary) => (
                    <option key={apiary.id} value={apiary.id}>{apiary.name}</option>
                  ))}
                </select>
              </label>
              <label className="block text-sm md:col-span-2">
                Nektar (valfri)
                <select className="mt-1 w-full" value={formNectarItemId} onChange={(event) => setFormNectarItemId(event.target.value)}>
                  <option value="">Ingen</option>
                  {(meta?.nectars ?? []).map((nectar) => (
                    <option key={nectar.id} value={nectar.id}>{nectar.label}</option>
                  ))}
                </select>
              </label>
              <label className="block text-sm md:col-span-2">
                Produkt
                <select className="mt-1 w-full" value={formProductId} onChange={(event) => setFormProductId(event.target.value)}>
                  <option value="">Välj produkt</option>
                  {productOptions.map((product) => (
                    <option key={product.id} value={product.id}>{product.name}</option>
                  ))}
                </select>
              </label>
            </div>
            <label className="block text-sm">
              Kommentar
              <textarea className="mt-1 min-h-[70px] w-full" value={formNotes} onChange={(event) => setFormNotes(event.target.value)} />
            </label>
            {renderOutputEditor()}
            <div className="rounded border border-dashed border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-600">
              Hink-ID sätts automatiskt när slungningen sparas. Synligt batch-ID blir alltid `containers.id`.
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn btn-secondary px-2 py-1" onClick={() => setCreateOpen(false)}>Avbryt</button>
              <button type="button" className="btn btn-success px-2 py-1" onClick={createExtraction} disabled={savingExtraction}>
                {savingExtraction ? 'Sparar...' : 'Skapa slungning'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-3xl space-y-3 rounded border border-gray-200 bg-white p-3">
            <h2 className="text-base font-semibold">Redigera slungning</h2>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              <label className="block text-sm">
                Datum
                <input type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD" className="mt-1 w-full" value={formDate} onChange={(event) => setFormDate(event.target.value)} />
              </label>
              <label className="block text-sm">
                Bigård (valfri)
                <select className="mt-1 w-full" value={formApiarySiteId} onChange={(event) => setFormApiarySiteId(event.target.value)}>
                  <option value="">Ingen</option>
                  {(meta?.apiaries ?? []).map((apiary) => (
                    <option key={apiary.id} value={apiary.id}>{apiary.name}</option>
                  ))}
                </select>
              </label>
              <label className="block text-sm md:col-span-2">
                Nektar (valfri)
                <select className="mt-1 w-full" value={formNectarItemId} onChange={(event) => setFormNectarItemId(event.target.value)}>
                  <option value="">Ingen</option>
                  {(meta?.nectars ?? []).map((nectar) => (
                    <option key={nectar.id} value={nectar.id}>{nectar.label}</option>
                  ))}
                </select>
              </label>
              <label className="block text-sm md:col-span-2">
                Produkt
                <select className="mt-1 w-full" value={formProductId} onChange={(event) => setFormProductId(event.target.value)}>
                  <option value="">Välj produkt</option>
                  {productOptions.map((product) => (
                    <option key={product.id} value={product.id}>{product.name}</option>
                  ))}
                </select>
              </label>
            </div>
            <label className="block text-sm">
              Kommentar
              <textarea className="mt-1 min-h-[70px] w-full" value={formNotes} onChange={(event) => setFormNotes(event.target.value)} />
            </label>
            {renderOutputEditor()}
            <div className="flex justify-end gap-2">
              <button type="button" className="btn btn-secondary px-2 py-1" onClick={() => setEditOpen(false)}>Avbryt</button>
              <button type="button" className="btn btn-success px-2 py-1" onClick={saveEdit} disabled={savingExtraction}>
                {savingExtraction ? 'Sparar...' : 'Spara ändringar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedId && !createOpen && !editOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-4xl space-y-3 rounded border border-gray-200 bg-white p-3">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-base font-semibold">
                {details?.extractionCode ?? (loadingDetails ? 'Laddar detalj...' : `Slungning #${selectedId}`)}
              </h2>
              <button type="button" className="btn btn-secondary px-2 py-1" onClick={() => setSelectedId(null)}>Stäng</button>
            </div>

            {loadingDetails && <p className="text-sm text-gray-500">Laddar detalj...</p>}

            {details && !loadingDetails && (
              <>
                <div className="flex items-start justify-between gap-3">
                  <div className="text-sm text-gray-600">
                    <div>Datum: {String(details.extractionDate).slice(0, 10)}</div>
                    <div>Bigård: {details.apiaryName ?? '-'}</div>
                    <div>Nektar: {details.nectarLabel ?? '-'}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {writable && <button type="button" className="btn btn-secondary px-2 py-1" onClick={openEdit}>Redigera</button>}
                    <span className={`rounded px-2 py-0.5 text-xs ${details.status === 'POSTED' ? 'bg-success/15 text-success' : 'bg-orange-100 text-orange-800'}`}>
                      {statusLabel(details.status)}
                    </span>
                  </div>
                </div>

                {details.notes && <p className="text-sm text-gray-700">{details.notes}</p>}

                <div className="space-y-1">
                  <div className="text-sm font-medium">Outputs</div>
                  {details.outputs.map((output) => (
                    <div key={output.id} className="rounded border border-gray-200 px-2 py-1 text-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-medium">{formatContainerDisplayId(output.containerId, output.containerCode) || String(output.containerId)}</div>
                          <div className="text-xs text-gray-600">
                            {output.productName} • {formatQty(output.qtyKg, 'kg')} kg • Vattenhalt: {output.waterPercent ?? '-'}% • Status: {output.statusLabel ?? '-'}
                          </div>
                        </div>
                        {writable && meta && (
                          <div className="min-w-48">
                            <select
                              className="w-full text-sm"
                              value={output.statusItemId ?? ''}
                              onChange={(event) => {
                                const nextStatusId = Number(event.target.value);
                                if (nextStatusId > 0) {
                                  void updateOutputStatus(output.containerId, nextStatusId);
                                }
                              }}
                              disabled={updatingContainerId === output.containerId}
                            >
                              <option value="">Välj status</option>
                              {meta.statuses.map((status) => (
                                <option key={status.id} value={status.id}>{status.label}</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="space-y-2 rounded border border-gray-200 bg-gray-50 p-3">
                  <div className="font-medium">Analysdokument</div>
                  {writable && (
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_150px_1fr_auto]">
                      <input className="w-full" placeholder="Titel, t.ex. Analys 1" value={documentDraft.title} onChange={(event) => setDocumentDraft((current) => ({ ...current, title: event.target.value }))} />
                      <input type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD" className="w-full" value={documentDraft.documentDate} onChange={(event) => setDocumentDraft((current) => ({ ...current, documentDate: event.target.value }))} />
                      <input className="w-full" placeholder="Anteckning (valfritt)" value={documentDraft.notes} onChange={(event) => setDocumentDraft((current) => ({ ...current, notes: event.target.value }))} />
                      <label className="inline-flex cursor-pointer items-center rounded border border-orange-500 px-3 py-1 text-sm hover:bg-orange-50">
                        {uploadingDocument ? 'Laddar upp...' : 'Ladda upp'}
                        <input
                          type="file"
                          accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.csv,.xlsx,.xls,.doc,.docx"
                          className="hidden"
                          disabled={uploadingDocument}
                          onChange={(event) => {
                            const file = event.target.files?.[0];
                            if (file) {
                              void uploadDocument(file);
                            }
                            event.target.value = '';
                          }}
                        />
                      </label>
                    </div>
                  )}

                  {details.documents.length === 0 ? (
                    <div className="text-sm text-gray-500">Inga dokument uppladdade ännu.</div>
                  ) : (
                    <div className="space-y-1">
                      {details.documents.map((document) => (
                        <div key={document.id} className="flex items-start justify-between gap-3 rounded border border-gray-200 bg-white px-2 py-1.5 text-sm">
                          <div className="min-w-0">
                            <div className="font-medium">{document.title || document.filename}</div>
                            <div className="text-xs text-gray-600">
                              {document.documentDate ?? '-'} • {document.filename} • {Math.round(document.sizeBytes / 1024)} kB
                            </div>
                            {document.notes && <div className="text-xs text-gray-600">{document.notes}</div>}
                          </div>
                          <div className="flex shrink-0 items-center gap-2">
                            <a className="rounded border border-gray-300 px-2 py-1 text-xs hover:bg-gray-50" href={extractionsApi.getDocumentUrl(document.id)} target="_blank" rel="noreferrer">
                              Öppna
                            </a>
                            {writable && (
                              <button
                                type="button"
                                className="rounded border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                                onClick={() => void deleteDocument(document.id)}
                                disabled={deletingDocumentId === document.id}
                              >
                                {deletingDocumentId === document.id ? 'Tar bort...' : 'Ta bort'}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {writable && details.status !== 'POSTED' && (
                  <div className="flex justify-end">
                    <button type="button" className="btn btn-success px-2 py-1" onClick={postExtraction} disabled={posting}>
                      {posting ? 'Bokför...' : 'Bokför slungning'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

